// « Signaler un problème » (DS §6.16, Task B2) : picto dans la barre d'app qui
// ouvre un `<dialog>` — texte libre, gravité vue par la personne, capture
// d'écran retirable, et tout ce qui part est énuméré avant l'envoi. Rien n'est
// écrit tant que personne ne signale (ADR 0025).
//
// Deux formes de déclencheur, portées par le même composant : le picto icône
// seule (`aria-label="Signaler un problème"`, usage courant dans AppHeader) et
// un bouton texte « Signaler ce problème » quand une `erreur` est déjà jointe
// (usage AppErrorBoundary) — la même chose vue depuis deux endroits, jamais
// deux dialogues.
//
// Le client Supabase est obtenu via `getSupabaseClient()` (pas `useAuth()`) :
// ce composant est rendu dans TOUTE variante d'AppHeader, y compris hors d'un
// `AuthProvider` (démonstrations du styleguide, écrans pré-auth) — il ne doit
// jamais exiger ce contexte pour simplement s'afficher.
//
// `Diagnostics`, `envoyerSignalement` (donc zod, via schema.ts), le client
// Supabase (`@supabase/supabase-js`, GoTrueClient/RealtimeClient…) et
// `CapacitorAudioRecorder` sont tous chargés PARESSEUSEMENT (imports
// dynamiques dans `ouvrir`/`envoyer`, jamais au sommet du fichier) :
// `diagnostics.ts` appelle `registerPlugin` à l'import (cassant les nombreux
// tests d'écrans qui remplacent `@capacitor/core` par un témoin minimal sans
// `registerPlugin`), et aucun de ces poids lourds n'a de raison d'alourdir le
// chunk d'AppHeader — SignalerProbleme est rendu sur TOUS les écrans, l'envoi
// n'a lieu que sur un geste explicite. Les libellés de gravité viennent de
// `gravites.ts` (SANS zod) précisément pour rester disponibles de façon
// synchrone (fieldset toujours affiché).

import { Capacitor } from "@capacitor/core";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { ScreenshotResult } from "../lib/native/diagnostics";
import { createBrouillon } from "../lib/signalement/brouillon";
import { lireContexte } from "../lib/signalement/contexte";
import { GRAVITE_LIBELLES, GRAVITES, type Gravite } from "../lib/signalement/gravites";
import { fil } from "./FilAriane";
import { Button } from "./ui/Button";
import { Card } from "./ui/Card";
import { Icon } from "./ui/Icon";
import { useModalDialogViewport } from "./use-modal-dialog-viewport";

export interface SignalerProblemeProps {
  /** Gravité pré-cochée à l'ouverture (ex. « bloque » depuis AppErrorBoundary). */
  gravite?: Gravite;
  /** Erreur technique déjà jointe (invisible, pas à retaper) — vient d'AppErrorBoundary. */
  erreur?: { message: string; pile: string | null } | null;
  /** Ouvre le dialogue dès le montage. Défaut `false` : un signalement ne s'impose jamais. */
  ouvrirAuMontage?: boolean;
}

type Phase = "form" | "envoi" | "echec" | "confirme";

const GRAVITE_DEFAUT: Gravite = "detail";

function sessionStorageOuNull(): Pick<Storage, "getItem" | "setItem" | "removeItem"> | null {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    // Mode privé, quota, ou storage bloqué par une politique du navigateur.
    return null;
  }
}

// Un seul brouillon en session : les deux instances possibles à l'écran (le
// picto ambiant du header + celle, pré-remplie, d'AppErrorBoundary) partagent
// la même sauvegarde — cohérent, puisqu'il n'y a jamais qu'un dialogue ouvert
// à la fois en pratique.
const brouillon = createBrouillon(sessionStorageOuNull());

export function SignalerProbleme({
  gravite: gravitePrereglee,
  erreur = null,
  ouvrirAuMontage = false,
}: SignalerProblemeProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const descId = useId();
  const graviteNom = useId();
  const captureId = useId();

  // Le hook de zone visible a besoin de savoir quand le dialogue est ouvert :
  // `showModal()` ne se reflète dans aucun état React.
  const [ouvert, setOuvert] = useState(false);
  useModalDialogViewport(dialogRef, ouvert);

  const [phase, setPhase] = useState<Phase>("form");
  const [description, setDescription] = useState("");
  const [gravite, setGravite] = useState<Gravite>(gravitePrereglee ?? GRAVITE_DEFAUT);
  const [ecran, setEcran] = useState<ScreenshotResult | null>(null);
  const [joindreCapture, setJoindreCapture] = useState(true);
  const [erreurEnvoi, setErreurEnvoi] = useState<string | null>(null);
  const [numeroEnvoye, setNumeroEnvoye] = useState<string | null>(null);

  function ecrireBrouillon(nextDescription: string, nextGravite: Gravite) {
    brouillon.ecrire({ description: nextDescription, gravite: nextGravite });
  }

  const ouvrir = useCallback(async () => {
    // Capture AVANT ouverture (DS §6.16) : la page telle qu'elle est, pas le
    // formulaire vide.
    const { Diagnostics } = await import("../lib/native/diagnostics");
    const capture = await Diagnostics.screenshot();
    setEcran(capture);
    setJoindreCapture(capture != null);

    const sauvegarde = brouillon.lire();
    setDescription(sauvegarde?.description ?? "");
    setGravite(sauvegarde?.gravite ?? gravitePrereglee ?? GRAVITE_DEFAUT);
    setErreurEnvoi(null);
    setNumeroEnvoye(null);
    setPhase("form");

    // Sur NATIF, on retient le focus sur le DIALOGUE plutôt que sur la zone de
    // texte : sinon `showModal()` vise le premier élément focusable — le
    // textarea — et iOS ouvre le clavier aussitôt, masquant « Envoyer », la
    // vignette de la capture et le « Ce qui part avec ton message » que §6.16
    // demande justement de faire lire AVANT d'envoyer. Le clavier monte au
    // premier toucher dans le champ, pas avant. Pour un lecteur d'écran c'est
    // aussi le bon point d'entrée : le nom du dialogue est annoncé au lieu
    // d'un champ vide. Sur le web il n'y a pas de clavier à l'écran, on garde
    // le confort du curseur déjà dans le champ (même arbitrage que
    // PersonPicker).
    //
    // L'attribut est posé sur le NŒUD, pas en JSX : React n'émet jamais
    // `autofocus`, il appelle `focus()` au montage — ce qui ne veut rien dire
    // pour un dialogue encore fermé (vérifié : l'attribut est absent du DOM).
    // Or les « dialog focusing steps » du HTML ne regardent QUE l'attribut.
    if (Capacitor.isNativePlatform()) dialogRef.current?.setAttribute("autofocus", "");
    dialogRef.current?.showModal();
    setOuvert(true);
  }, [gravitePrereglee]);

  useEffect(() => {
    if (ouvrirAuMontage) void ouvrir();
  }, [ouvrirAuMontage, ouvrir]);

  function fermer() {
    dialogRef.current?.close();
  }

  async function envoyer() {
    setPhase("envoi");
    try {
      const contexte = lireContexte({
        location: window.location,
        navigator: window.navigator,
        screen: window.screen,
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,
        devicePixelRatio: window.devicePixelRatio,
        now: new Date(),
        version: __APP_VERSION__,
        natif: Capacitor.isNativePlatform(),
      });
      const { Diagnostics } = await import("../lib/native/diagnostics");
      const appareil = await Diagnostics.deviceInfo();

      // Diagnostics de l'enregistreur : natif seulement — sur le web la
      // méthode n'existe pas, et un échec (n'importe quelle plateforme) ne
      // doit jamais empêcher l'envoi du reste du signalement.
      let diagnostics: unknown = null;
      if (Capacitor.isNativePlatform()) {
        try {
          const { CapacitorAudioRecorder } = await import("@capgo/capacitor-audio-recorder");
          diagnostics = await CapacitorAudioRecorder.getDiagnostics();
        } catch {
          diagnostics = null;
        }
      }

      const [{ envoyerSignalement }, { getSupabaseClient }] = await Promise.all([
        import("../lib/signalement/envoyer"),
        import("../lib/supabase/client"),
      ]);
      const resultat = await envoyerSignalement({
        client: getSupabaseClient(),
        description,
        gravite,
        contexte,
        appareil,
        fil: fil.list(),
        diagnostics,
        erreur,
        capture:
          joindreCapture && ecran ? { base64: ecran.base64, mimeType: ecran.mimeType } : null,
      });

      if (resultat.ok) {
        brouillon.effacer();
        setNumeroEnvoye(resultat.numero);
        setPhase("confirme");
      } else {
        setErreurEnvoi(resultat.error);
        setPhase("echec");
      }
    } catch (err) {
      console.error("[SignalerProbleme] envoi échoué :", err);
      setErreurEnvoi("L'envoi a échoué. Ton texte est conservé.");
      setPhase("echec");
    }
  }

  function fermerConfirmation() {
    setPhase("form");
    setDescription("");
    setGravite(gravitePrereglee ?? GRAVITE_DEFAUT);
    setNumeroEnvoye(null);
    fermer();
  }

  const descriptionValide = description.trim().length >= 10;

  return (
    <>
      {erreur ? (
        <Button type="button" variant="ghost" size="sm" onClick={() => void ouvrir()}>
          Signaler ce problème
        </Button>
      ) : (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Signaler un problème"
          onClick={() => void ouvrir()}
        >
          <Icon name="message-square-warning" size={18} />
        </Button>
      )}

      <dialog
        ref={dialogRef}
        data-fil="ignorer"
        aria-label="Signaler un problème"
        // Focusable sans être dans l'ordre de tabulation : c'est ce qui permet
        // aux « dialog focusing steps » de retenir le focus sur le dialogue
        // lui-même sur natif (voir `ouvrir`).
        tabIndex={-1}
        onClose={() => setOuvert(false)}
        // Gouttière et respect des zones sûres : règle `dialog:modal` du DS
        // (§6.17, global.css). Ici, seulement la largeur maximale du panneau.
        className="w-full max-w-md rounded-lg border border-line bg-surface p-4 text-ink"
      >
        {phase === "confirme" && numeroEnvoye ? (
          <Card variant="success" className="flex flex-col items-start gap-2">
            <p className="m-0 text-body font-semibold text-ink">Signalement envoyé</p>
            <p className="m-0 font-display text-hero tabular-nums text-flambeau-700">
              {numeroEnvoye}
            </p>
            <p className="m-0 text-small text-muted">Garde ce numéro si tu veux en reparler.</p>
            <Button type="button" variant="primary" size="sm" onClick={fermerConfirmation}>
              Fermer
            </Button>
          </Card>
        ) : (
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              void envoyer();
            }}
          >
            <div className="flex flex-col gap-1">
              <label htmlFor={descId} className="text-small font-medium text-ink">
                Que se passe-t-il ?
              </label>
              <textarea
                id={descId}
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  ecrireBrouillon(e.target.value, gravite);
                }}
                rows={3}
                minLength={10}
                maxLength={4000}
                disabled={phase === "envoi"}
                className="w-full resize-none rounded-lg border border-line bg-surface px-3 py-2 text-body text-ink placeholder:text-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-orange focus-visible:border-flambeau-500"
              />
              <p className="m-0 text-small text-muted">{description.length} / 4000</p>
            </div>

            <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
              <legend className="p-0 text-small font-medium text-ink">Où en es-tu ?</legend>
              {GRAVITES.map((g) => (
                <label key={g} className="flex items-center gap-2 text-small text-ink">
                  <input
                    type="radio"
                    name={graviteNom}
                    value={g}
                    checked={gravite === g}
                    disabled={phase === "envoi"}
                    onChange={() => {
                      setGravite(g);
                      ecrireBrouillon(description, g);
                    }}
                  />
                  {GRAVITE_LIBELLES[g]}
                </label>
              ))}
            </fieldset>

            {ecran && (
              <div className="flex flex-col gap-2">
                {/* `object-contain` sur un fond neutre, jamais `object-cover` :
                    une capture de téléphone est très haute (≈ 9:19,5) — la
                    recadrer dans 192 px de haut n'en montrait qu'une bande
                    centrale, où personne ne reconnaissait son écran. La
                    vignette doit montrer CE QUI PART, en entier. */}
                <img
                  src={`data:${ecran.mimeType};base64,${ecran.base64}`}
                  alt="Capture d'écran de la page"
                  className="h-48 w-full rounded-lg border border-line bg-ground object-contain p-1"
                />
                <label
                  htmlFor={captureId}
                  className="flex items-center gap-2 text-label text-muted"
                >
                  <input
                    id={captureId}
                    type="checkbox"
                    checked={!joindreCapture}
                    disabled={phase === "envoi"}
                    onChange={(e) => setJoindreCapture(!e.target.checked)}
                  />
                  Ne pas joindre la capture d'écran
                </label>
              </div>
            )}

            <details className="rounded-md border border-line bg-flambeau-50 p-3 text-small text-ink">
              <summary className="cursor-pointer font-medium">Ce qui part avec ton message</summary>
              <ul className="m-0 mt-2 flex list-disc flex-col gap-1 pl-5 text-label text-muted">
                <li>Page et appareil</li>
                <li>Courriel connecté</li>
                <li>Tes 30 derniers gestes (sans aucune valeur saisie)</li>
                <li>État de l'enregistreur audio</li>
                <li>La capture d'écran (sauf si retirée ci-dessus)</li>
              </ul>
            </details>

            {phase === "echec" && (
              <Card variant="danger">
                <p className="m-0 text-small">{erreurEnvoi}</p>
              </Card>
            )}

            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={fermer}>
                Fermer
              </Button>
              {phase === "echec" ? (
                <Button type="submit" variant="secondary" size="sm">
                  Réessayer
                </Button>
              ) : (
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  disabled={!descriptionValide || phase === "envoi"}
                >
                  {phase === "envoi" ? "Envoi…" : "Envoyer"}
                </Button>
              )}
            </div>
          </form>
        )}
      </dialog>
    </>
  );
}
